import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { debounce } from "lodash";
import { JobType } from "../../apiResources/Job";
import { EngagementStatus } from "../../apiResources/Engagement";
import {
  SearchFilterContainer,
  SearchFilterProvider,
  SearchFilterGroup,
  SearchFilterOption,
} from "../library/SearchFilter";
import EngagementListSelectedFilters from "./EngagementListSelectedFilters";
import { FilterData } from "./EngagementsListPage";
import CurrentUser from "../../apiResources/CurrentUser";
import { isFeatureEnabled } from "../../helpers";

const EngagementListFilterBar = (props: Props) => {
  const {
    initialQuery,
    onQueryChange,
    onFiltersChange,
    filters,
    filteringEnabled = true,
    user,
  } = props;
  const [query, setQuery] = useState(initialQuery);

  const debouncedQueryChange = useMemo(() => debounce(onQueryChange, 350), []);

  useEffect(() => {
    debouncedQueryChange.cancel();
    debouncedQueryChange(query);
  }, [query]);

  return (
    <SearchFilterProvider initialValue={filters} onChange={onFiltersChange}>
      <SearchBar id="engagements-filter-bar">
        <SearchInput
          placeholder="Search for a job"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        {filteringEnabled && (
          <SearchFilterContainer>
            <SearchFilterGroup label="Status" defaultCollapsed={false}>
              <SearchFilterOption name="status" value={"all"} single>
                Show all
              </SearchFilterOption>
              <SearchFilterOption
                single
                name="status"
                value={EngagementStatus.ACCEPTED}
              >
                Active
              </SearchFilterOption>
              {(!user?.isPartnerUser ||
                (isFeatureEnabled("displayPartnerEngagementsInYourJobs") &&
                  user?.isPartnerUser)) && (
                <SearchFilterOption
                  single
                  name="status"
                  value={EngagementStatus.PENDING}
                >
                  Pending
                </SearchFilterOption>
              )}
              <SearchFilterOption
                single
                name="status"
                value={EngagementStatus.DISENGAGED}
              >
                Disengaged
              </SearchFilterOption>
              <SearchFilterOption
                single
                name="status"
                value={EngagementStatus.REJECTED}
              >
                Rejected
              </SearchFilterOption>
              <SearchFilterOption single name="status" value="filled">
                Filled
              </SearchFilterOption>
              <SearchFilterOption single name="status" value="closed">
                Closed
              </SearchFilterOption>
            </SearchFilterGroup>

            <SearchFilterGroup label="Job Type">
              <SearchFilterOption name="jobType" value={Object.values(JobType)}>
                Select all
              </SearchFilterOption>
              <SearchFilterOption name="jobType" value={JobType.PERMANENT}>
                Permanent
              </SearchFilterOption>
              <SearchFilterOption name="jobType" value={JobType.CONTRACT}>
                Contract
              </SearchFilterOption>
            </SearchFilterGroup>
          </SearchFilterContainer>
        )}
      </SearchBar>

      <EngagementListSelectedFilters filters={filters} />
    </SearchFilterProvider>
  );
};

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 3px;
  margin-right: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.gray700};
`;

const SearchBar = styled.div`
  display: flex;
  border-radius: 3px;
  margin-bottom: 1rem;
`;

type Props = {
  initialQuery: string;
  onQueryChange: (query: string) => void;
  onFiltersChange: (filters: FilterData) => void;
  filters: FilterData;
  filteringEnabled: boolean;
  user: CurrentUser;
};

export default EngagementListFilterBar;
